// src/components/CustomerFlow/CustomerFlow.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
// Importar estilos base de PurchaseFlow
import '../PurchaseFlow/PurchaseFlow.css';
// Importar estilos específicos de CustomerFlow
import './CustomerFlow.css';

// Importar todos los componentes de pasos
import IdentityValidationStep from './Steps/IdentityValidationStep';
import PaymentProcessingStep from './Steps/PaymentProcessingStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import DocumentUploadStep from './Steps/DocumentUploadStep';
import FinalStep from './Steps/FinalStep';
import ErrorBoundary from '../Common/ErrorBoundary';
import { trackMobileCustomerFlowStepEntered, trackMobileCustomerFlowStepCompleted, trackMobileCustomerFlowCompleted } from '../../analytics/events';


const CustomerFlow = ({ 
  flowData, 
  stepStatuses, 
  timeRemaining, 
  onStepComplete,
  onStepBack,
  onSessionExpired,
  searchParams,
  onStepperUiChange,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [formData, setFormData] = useState({});
  const trackedStepsRef = useRef(new Set()); // Track which steps have been tracked for "entered" event

  // Mapeo de pasos a componentes
  const stepComponentMap = {
    'ValidaStep': IdentityValidationStep,
    'PaymentStep': PaymentProcessingStep,
    'ConfirmationStep': ConfirmationStep,
    'DocumentUploadStep': DocumentUploadStep
  };

  useEffect(() => {
    if (flowData?.flow_config && Object.keys(stepStatuses).length > 0) {
      const stepsArray = flowData.flow_config.map(stepName => stepComponentMap[stepName]).filter(Boolean);
      
      // El FinalStep siempre está disponible para todos los flujos (pero no se muestra en el stepper)
      stepsArray.push(FinalStep);
      
      setSteps(stepsArray);
      
      // Determinar el paso actual basado en prioridades
      const targetStepIndex = determineCurrentStepIndex();
      setCurrentStepIndex(targetStepIndex);
      
      // Track step entered when flow initializes (only once per step)
      if (targetStepIndex < stepsArray.length && flowData?.flow_id) {
        const stepName = flowData.flow_config[targetStepIndex];
        if (stepName) {
          const stepKey = `${flowData.flow_id}-${stepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId from API
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
      }
    }
  }, [flowData, stepStatuses]);

  useEffect(() => {
    if (!onStepperUiChange) return;

    const flowStepNames = flowData?.flow_config;
    if (!steps.length || !flowStepNames?.length || flowStepNames.length <= 1) {
      onStepperUiChange(null);
      return;
    }

    onStepperUiChange({
      flowStepNames,
      stepStatuses,
      currentStepIndex,
    });
  }, [
    onStepperUiChange,
    steps.length,
    flowData?.flow_config,
    stepStatuses,
    currentStepIndex,
  ]);

  useEffect(() => {
    if (!onStepperUiChange) return;
    return () => onStepperUiChange(null);
  }, [onStepperUiChange]);

  /**
   * Determina el índice del paso actual basado en prioridades
   * Prioridad: in_progress > pending > siguiente después del último procesado > FinalStep
   */
  const determineCurrentStepIndex = () => {
    if (!flowData?.flow_config || Object.keys(stepStatuses).length === 0) {
      return 0;
    }

    const stepNames = flowData.flow_config;
    
    // 1. Buscar paso en progreso (prioridad máxima)
    const inProgressIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'in_progress'
    );
    if (inProgressIndex !== -1) {
      return inProgressIndex;
    }
    
    // 2. Si todos los pasos están completados, mostrar el paso final (ALTA PRIORIDAD)
    // Nota: 'processed' se considera como un estado válido de completado
    const allStepsCompleted = stepNames.every(stepName => 
      stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed'
    );
    
    if (allStepsCompleted) {
      // El FinalStep está en el último índice (después de todos los pasos del flujo)
      return stepNames.length; // El paso final está en el último índice
    }
    
    // 3. Buscar primer paso pending
    const pendingIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'pending'
    );
    if (pendingIndex !== -1) {
      return pendingIndex;
    }
    
    // 4. Buscar siguiente paso después del último procesado/completado
    // Nota: 'processed' se trata como un estado intermedio que permite avanzar
    const lastProcessedIndex = stepNames.findIndex(stepName => 
      stepStatuses[stepName] === 'processed' || stepStatuses[stepName] === 'completed'
    );
    
    if (lastProcessedIndex >= 0 && lastProcessedIndex < stepNames.length - 1) {
      return lastProcessedIndex + 1;
    }
    
    // 5. Fallback: primer paso
    return 0;
  };

  const handleNext = useCallback((data, stepStatus = null) => {
    setFormData((prevData) => ({
      ...prevData,
      ...data,
    }));

    if (stepStatus === 'completed' || stepStatus === 'processed' || stepStatus === 'in_progress') {
      if (stepStatus === 'completed' || stepStatus === 'processed') {
        const stepName = flowData?.flow_config?.[currentStepIndex];
        if (stepName && flowData?.flow_id) {
          const stepCompletedKey = `${flowData.flow_id}-${stepName}-completed`;
          if (!trackedStepsRef.current.has(stepCompletedKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepCompleted({
              commerceId: branchId || 'mobile_flow',
              branchId: branchId || undefined,
              stepName,
              flowId: flowData.flow_id,
            });
            trackedStepsRef.current.add(stepCompletedKey);
          }
        }
      }

      onStepComplete(data);

      setTimeout(() => {
        const newStepIndex = determineCurrentStepIndex();
        setCurrentStepIndex(newStepIndex);

        const newStepName = flowData?.flow_config?.[newStepIndex];
        if (newStepName && flowData?.flow_id) {
          const stepKey = `${flowData.flow_id}-${newStepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow',
              branchId: branchId || undefined,
              stepName: newStepName,
              flowId: flowData.flow_id,
            });
            trackedStepsRef.current.add(stepKey);
          }
        }

        if (newStepIndex === steps.length - 1 && flowData?.flow_id) {
          const branchId = localStorage.getItem('temp_tokenBranchId');
          trackMobileCustomerFlowCompleted({
            commerceId: branchId || 'mobile_flow',
            branchId: branchId || undefined,
            flowId: flowData.flow_id,
          });
        }
      }, 100);
    }
  }, [currentStepIndex, flowData, onStepComplete, steps.length, stepStatuses]);

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      onStepBack();
    }
  };

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      return <div>No pudimos cargar este paso. Intentá de nuevo.</div>;
    }

    // Props específicas para el paso final
    if (CurrentStepComponent === FinalStep) {
      return (
        <FinalStep
          onComplete={handleNext}
        />
      );
    }

    const stepContent = (
      <CurrentStepComponent
        flowData={flowData}
        stepStatuses={stepStatuses}
        timeRemaining={timeRemaining}
        onNext={handleNext}
        onBack={handleBack}
        onSessionExpired={onSessionExpired}
        formData={formData}
        currentStepIndex={currentStepIndex}
        totalSteps={steps.length}
        searchParams={searchParams}
      />
    );

    if (CurrentStepComponent === PaymentProcessingStep) {
      return (
        <ErrorBoundary message="No pudimos mostrar la pantalla de pago. Recargá la página e intentá de nuevo.">
          {stepContent}
        </ErrorBoundary>
      );
    }

    return stepContent;
  };

  if (!steps.length) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="text-muted">Preparando tu compra...</p>
      </div>
    );
  }

  return (
    <div className="customer-flow">
      {renderStep()}
    </div>
  );
};

export default CustomerFlow;
